import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '@/app/error-boundary';
import { AppShell } from '@/components/layout/app-shell';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { HomePage } from '@/pages/home-page';

const RoomPage = lazy(() =>
  import('@/pages/room-page').then((module) => ({ default: module.RoomPage })),
);
const TransfersPage = lazy(() =>
  import('@/pages/transfers-page').then((module) => ({ default: module.TransfersPage })),
);
const HistoryPage = lazy(() =>
  import('@/pages/history-page').then((module) => ({ default: module.HistoryPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/settings-page').then((module) => ({ default: module.SettingsPage })),
);
const OfflinePage = lazy(() =>
  import('@/pages/offline-page').then((module) => ({ default: module.OfflinePage })),
);
const AboutPage = lazy(() =>
  import('@/pages/about-page').then((module) => ({ default: module.AboutPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/not-found-page').then((module) => ({ default: module.NotFoundPage })),
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);
  return null;
}

function PageFallback() {
  return (
    <div className="space-y-4 pt-2">
      <div className="h-4 w-28 animate-pulse rounded-full bg-muted/60" />
      <div className="h-9 w-full max-w-64 animate-pulse rounded-xl bg-muted/50" />
      <div className="h-4 w-full max-w-80 animate-pulse rounded-full bg-muted/30" />
      <div className="mt-6 h-64 animate-pulse rounded-2xl bg-muted/25" />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <TooltipProvider delayDuration={180}>
          <ScrollToTop />
          <AppShell>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/r/:code" element={<RoomPage />} />
                <Route path="/transfers" element={<TransfersPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/offline" element={<OfflinePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </AppShell>
          <Toaster />
        </TooltipProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
